import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";

import { Meal } from '../../api/meals';
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TimePicker from "material-ui-pickers/TimePicker";
import { formatDate } from "../../util/date-utility";
import { closeAddReservationDialog } from "../../actions/store/app-actions";
import {
	addReservation,
	updateSingleReservation,
	deleteReservation
} from "../../actions/reservation-actions";
import { Reservation } from "../../api/reservations";
import { fixTime, getReadablePaymentStatus } from "../../util/general-utility";
import { objectMatches } from "../../util/object-utility";
import { ConnectedDatePicker } from "../datepicker/index";
import { ConnectedDeleteReservationDialog } from "./delete-reservation-dialog";
import { Capabilities } from "../../integration/capabilities";
import { isIPhoneX } from '../../util/device-util';

interface State extends Reservation {
	shouldDelete: boolean;
	isClickable: boolean;
}

export interface AddReservationDialogProps {}

interface ConnectedAddReservationDialogProps extends WithStyles<typeof styles> {
	appDate: Date;
	open: boolean;
	reservationData: Reservation;
	capabilities: Capabilities;
	layout: "desktop" | "tablet" | "phone";
	availableMeals: Meal[];
	close: () => void;
}

type Props = AddReservationDialogProps &
	ConnectedAddReservationDialogProps &
	InjectedTranslateProps;

const initialState: State = {
	id: null,
	tableId: null,
	inquiryId: null,
	date: null,
	peopleCount: null,
	primaryName: "",
	time: null,
	phoneNumber: "",
	email: "",
	notes: "",
	meals: null,
	inquiredArea: null,
	checkedIn: false,
	shouldDelete: false,
	isClickable: false,
	payment: null
};

export class AddReservationDialog extends React.Component<Props, State> {
	public state: State = {
		...initialState,
		date: this.props.appDate
	};

	private initialReservationData: Reservation = null;
	private timer: number = null;

	componentWillReceiveProps(nextProps: Props) {
		if (this.props.open !== nextProps.open) {
			const { reservationData } = nextProps;

			if (nextProps.open) {
				this.setState({
					date: nextProps.appDate
				});

				clearTimeout(this.timer);
				this.timer = setTimeout(() => {
					this.setState({
						isClickable: true
					});
				}, 300);
			}

			if (reservationData !== null) {
				const data = {
					...reservationData,
					time: fixTime(reservationData.time)
				};

				this.initialReservationData = data;
				this.setState(data);
			}
		}
	}

	close() {
		this.setState({
			...initialState,
			date: this.props.appDate
		});

		this.props.close();
	}

	handleTimeChange = (date: Date) => {
		this.setState({ time: date });
	};

	handleDateChange = (date: Date) => {
		this.setState({ date });
	};

	handlePeopleCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const val = Number(event.target.value);

		this.setState({
			peopleCount: val > 0 ? val : 0
		});
	};

	handleCancelClick = () => {
		this.close();
	};

	handleDeleteClick = () => {
		this.setState({
			shouldDelete: true
		});
	};

	handleDeletionDialogNoClick = () => {
		this.setState({
			shouldDelete: false
		});
	};

	handleDeletionDialogYesClick = () => {
		deleteReservation(this.state.id);

		this.close();
	};

	handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		const { reservationData } = this.props;

		const data = { ...this.state };
		delete data.shouldDelete;
		delete data.isClickable;
		delete data.payment;
		delete data.meals;

		if (data.date) {
			data.date.setHours(0, 0, 0, 0);
		}

		if (reservationData !== null) {
			if (objectMatches(data, this.initialReservationData) === false) {
				updateSingleReservation(data.id, data);
			}
		} else {
			addReservation(data);
		}

		this.close();
	};

	render() {
		const {
			classes,
			t,
			open,
			reservationData,
			layout,
			capabilities,
			availableMeals
		} = this.props;
		const {
			peopleCount,
			date,
			primaryName,
			time,
			phoneNumber,
			email,
			notes,
			checkedIn,
			shouldDelete,
			isClickable,
			payment,
			meals
		} = this.state;

		return (
			<React.Fragment>
				<Dialog
					className={classNames(classes.dialog, {
						[classes.dialogClickable]: isClickable
					})}
					open={open}
					fullScreen={layout === "phone"}
				>
					<form className={classes.form} onSubmit={this.handleSubmit}>
						<DialogTitle>
							{reservationData
								? "Reservierung bearbeiten"
								: "Neue Reservierung"}
						</DialogTitle>
						<DialogContent>
							<div className={classNames(classes.formRow)}>
								<TextField
									type="number"
									className={classNames(
										classes.textField,
										classes.peopleCountInput
									)}
									id="reservationPeopleCount"
									label="Personenanzahl"
									value={peopleCount ? peopleCount : ""}
									onChange={this.handlePeopleCountChange}
									required
								/>
								<ConnectedDatePicker
									onChange={this.handleDateChange}
									value={date}
									datepickerProps={{
										id: "reservationDate",
										label: "Datum",
										required: true,
										className: classNames(
											classes.textField,
											classes.datepicker
										),
										InputProps: {
											value: formatDate(date)
										}
									}}
								/>
							</div>
							<div className={classes.formRow}>
								<TextField
									className={classNames(
										classes.textField,
										classes.primaryNameInput
									)}
									id="reservationPrimaryName"
									label="Name"
									value={primaryName}
									onChange={event =>
										this.setState({
											primaryName: event.target.value
										})
									}
									required
								/>
								<TimePicker
									className={classNames(
										classes.textField,
										classes.timepicker
									)}
									id="reservationTime"
									label="Uhrzeit"
									cancelLabel="Abbrechen"
									okLabel="OK"
									value={time}
									onChange={this.handleTimeChange}
									ampm={false}
								/>
							</div>
							<div className={classes.formRow}>
								<TextField
									className={classNames(
										classes.textField,
										classes.phoneNumberInput
									)}
									id="reservationPhoneNumber"
									label="Telefonnummer"
									value={phoneNumber}
									onChange={event =>
										this.setState({
											phoneNumber: event.target.value
										})
									}
								/>
								<TextField
									className={classNames(
										classes.textField,
										classes.emailInput
									)}
									id="reservationEmail"
									label="E-Mail Adresse"
									value={email}
									type="email"
									onChange={event =>
										this.setState({
											email: event.target.value
										})
									}
								/>
							</div>
							<div className={classes.formRow}>
								<TextField
									className={classNames(
										classes.textField,
										classes.notesInput
									)}
									id="reservationNotes"
									label="Notizen &amp; Anmerkungen"
									fullWidth
									multiline
									rowsMax={4}
									value={notes}
									onChange={event =>
										this.setState({
											notes: event.target.value
										})
									}
								/>
							</div>
							{meals && Object.keys(meals).length > 0 && (
								<div className={classes.formRow}>
									<FormGroup className={classes.textField}>
										<FormLabel className={classes.formLabel}>Speisen</FormLabel>
										<div className={classes.formText}>
											{Object.keys(meals).map(id => (
												<div key={id}>{meals[id]}x {availableMeals.find(m => m.id === id).name}</div>
											))}
										</div>
									</FormGroup>
								</div>
							)}
							{payment && (
								<div className={classes.formRow}>
									<FormGroup className={classes.textField}>
										<FormLabel className={classes.formLabel}>Zahlung</FormLabel>
										<div className={classes.formText}>
											<span className={classNames(classes.paymentState, {
												[classes.paymentNotCompleted]: payment.state !== "completed",
											})}>
												{getReadablePaymentStatus(payment)}
											</span>
										</div>
									</FormGroup>
								</div>
							)}
							<div className={classes.formRow}>
								<FormGroup
									className={classNames(
										classes.textField
									)}
								>
									<FormControlLabel
										control={
											<Checkbox
												id="checkedIn"
												color="primary"
												checked={checkedIn}
												onChange={event =>
													this.setState({
														checkedIn: event.target.checked
													})
												}
											/>
										}
										label="Eingecheckt"
									/>
								</FormGroup>
							</div>
						</DialogContent>
						<DialogActions>
							{reservationData && (
								<Button
									className={classes.deleteButton}
									onClick={this.handleDeleteClick}
								>
									Löschen
								</Button>
							)}
							<Button
								className={classes.cancelButton}
								onClick={this.handleCancelClick}
							>
								Abbrechen
							</Button>
							<Button
								className={classes.submitButton}
								color="primary"
								type="submit"
							>
								{reservationData ? "Speichern" : "OK"}
							</Button>
						</DialogActions>
					</form>
				</Dialog>
				<ConnectedDeleteReservationDialog
					open={shouldDelete}
					peopleCount={peopleCount}
					primaryName={primaryName}
					onNo={this.handleDeletionDialogNoClick}
					onYes={this.handleDeletionDialogYesClick}
				/>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	appDate: state.date,
	open: state.addReservationDialogOpen,
	reservationData: state.reservationDialogReservationData,
	capabilities: state.capabilities,
	layout: state.layout,
	availableMeals: state.settings.meals
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	close: () => dispatch(closeAddReservationDialog())
});

const styles = (theme: Theme) =>
	createStyles({
		dialog: {
			"-webkit-user-select": "none",
			userSelect: "none",
			pointerEvents: "none"
		},
		dialogClickable: {
			pointerEvents: "all"
		},
		textField: {
			margin: 10,

			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		},
		form: {
			height: "100%",
			display: "flex",
			flexDirection: "column"
		},
		formRow: {
			display: "flex",
			margin: -10,

			"@media (max-width: 600px)": {
				display: "block"
			}
		},
		peopleCountInput: {},
		datepicker: {},
		primaryNameInput: {},
		timepicker: {},
		phoneNumberInput: {},
		emailInput: {},
		notesInput: {},
		cancelButton: {},
		deleteButton: {
			marginRight: "auto"
		},
		submitButton: {},
		formLabel: {
			paddingTop: "5px",
			transform: "translate(0, 1.5px) scale(0.75)",
			transformOrigin: "top left"
		},
		formText: {
			paddingTop: "2px",
			color: "rgba(0, 0, 0, 0.87)",
			fontSize: "1.1428571428571428rem",
			fontFamily: "'Roboto', 'Helvetica', 'Arial', 'sans-serif'",
			lineHeight: "1.1875em"
		},
		paymentState: { color: "green" },
		paymentNotCompleted: { color: "black" }
	});

export const ConnectedAddReservationDialog = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(AddReservationDialog)
	)
);
